<template>
  <div class="px-10 ml-13">
    <card title="Filtros">
      <div class="row mt-n2">
        <SearchFieldSelector
          :default-start-date="today"
          :default-end-date="today"
          :text-value="dateQuery"
          :date-range-value="dateSelected"
          :clean-date="cleanDate"
          :options="dateSelectOptions"
          :default-option-index="defaultOptionIndex"
          @updateSearchValue="setDate"
        />

        <FieldContainer
          v-if="isAllowedCollectionPoint"
          label="Posto de coleta"
        >
          <SelectSearch
            id="txtCollectionPointName"
            ref="refSearchCollectionPoint"
            :value="collectionPoint"
            :options="collectionPointOptions"
            placeholder="Selecione um posto de coleta"
            :uppercase-all-items="true"
            :footer-enabled="false"
            :no-results-message-enabled="true"
            :icon-visible="false"
            no-results-text="Não foi encontrado posto de coleta cadastrado com esse nome"
            @change="setCollectionPoint"
          />
        </FieldContainer>

        <md-button
          class="btn btn-primary"
          @click="submitSearch"
        >
          <svg-loader :src="'/static/images/icon-generate-list.svg'" />
          Listar
        </md-button>
      </div>
      <div class="align-center">
        <md-layout class="flex-row mt-4 gap-4">
          <button-icon
            v-for="iconItem in icons"
            :key="iconItem.label"
            :color="iconItem.color"
            :label="iconItem.label"
            :src-icon="iconItem.src"
            :value="iconItem.value"
            :hint="hintPackagingType(iconItem.value)"
            @input="(event) => setPackagingType(iconItem, event)"
          />
        </md-layout>
      </div>
    </card>

    <CollectionPointModal
      title="Defina um posto de coleta"
      hide-button
      :show-modal="showModalCollectionPointModal"
      @change="setCollectionPointModal"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import Cookies from "js-cookie";
import notify from "@/api/notification";

import { formatDate } from "@/utils/date-utils";
import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
} from "@/enums";

import Card from "@/components/common/Card.vue";
import ButtonIcon from "@/components/common/ButtonIcon.vue";
import SearchFieldSelector from "@/components/common/SearchFieldSelectorV2.vue";
import FieldContainer from "@/components/common/FieldContainer.vue";
import SelectSearch from "@/components/common/SelectSearch.vue";
import CollectionPointModal from "../CollectionPointModal.vue";

export default {
  name: "BatchesFilter",

  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    FieldContainer,
    SelectSearch,
    CollectionPointModal,
  },

  data() {
    return {
      refSearchCollectionPoint: null,

      today: new Date(),
      dateSelected: null,
      typeDateSelected: null,
      dateQuery: "",
      cleanDate: 0,
      defaultOptionIndex: 0,

      packagingSelected: [],
      collectionPoint: null,
      integratedRoot: "",

      showModalCollectionPointModal: false,

      limitDaysBatches: 10,
      isResetting: false,

      packagingFrozenId: BATCH_PACKAGING_FROZEN_ID.toString(),
      packagingRefrigeratedId: BATCH_PACKAGING_REFRIGERATED_ID.toString(),
      packagingTemperatureId: BATCH_PACKAGING_ROOM_TEMPERATURE_ID.toString(),
      packagingRefrigeratedDryId: BATCH_PACKAGING_REFRIGERATED_DRY_ID.toString(),

      icons: [
        {
          id: BATCH_PACKAGING_FROZEN_ID.toString(),
          value: true,
          label: "Congelado",
          color: undefined,
          src: undefined,
        },
        {
          id: BATCH_PACKAGING_REFRIGERATED_ID.toString(),
          value: true,
          label: "Refrigerado",
          color: "background-color-royal-blue",
          src: "/static/images/icon-refrigerated.svg",
        },
        {
          id: BATCH_PACKAGING_ROOM_TEMPERATURE_ID.toString(),
          value: true,
          label: "Ambiente",
          color: "background-color-jaffa",
          src: "/static/images/icon-room-temperature.svg",
        },
        {
          id: BATCH_PACKAGING_REFRIGERATED_DRY_ID.toString(),
          value: true,
          label: "Refrigerado Seco",
          color: "background-color-shakespeare",
          src: "/static/images/icon-refrigerated-dry.svg",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["collectionPointLabels", "toggle", "webSocketStatus"]),

    collectionPointOptions() {
      if (!this.collectionPointLabels || this.collectionPointLabels.length === 0) {
        return [];
      }

      const allOption = {
        label: "Todos",
        value: "Todos",
        id: "ALL",
      };

      return [allOption, ...this.collectionPointLabels];
    },

    isAllowedCollectionPoint() {
      const collectPlace = this.toggle?.collectPlace;
      return collectPlace && this.collectionPointLabels?.length > 0;
    },
    startDate() {
      return formatDate(this.dateSelected?.start, 4);
    },
    endDate() {
      return formatDate(this.dateSelected?.end, 4);
    },

    limitDate() {
      return moment(this.today, "DD/MM/YYYY").subtract(this.limitDaysBatches, "days").toDate();
    },

    dateSelectOptions() {
      return [
        {
          itemValue: "TD",
          value: "transferDate",
          label: "Período",
          placeholder: "Período",
          limitDays: this.limitDaysBatches,
          limitDate: this.limitDate,
          type: "date",
        },
      ];
    },

    batchFilter() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        packagingType: this.packagingSelected,
        integrated: this.integratedRoot,
        collectionPointName: this.collectionPoint?.value === "Todos" ? undefined : this.collectionPoint?.label,
      };
    },
  },

  watch: {
    dateSelected(newValue) {
      if (newValue) {
        this.dateQuery = `${moment(newValue.start).format("DD/MM/YYYY")} - ${moment(newValue.end).format(
          "DD/MM/YYYY"
        )}`;
      }
    },
  },

  async mounted() {
    await this.getAllCollectionPointList();
    this.getSelectedCollectionPoint();
    this.setInitialFilters();
    this.$nextTick(() => {
      this.submitSearch();
    });
  },

  methods: {
    ...mapActions(["selectBatchList", "batchGetBatches", "getAllCollectionPointList"]),

    setInitialFilters() {
      this.typeDateSelected = "TD";
      this.dateSelected = { start: this.today, end: this.today };
      this.packagingSelected.push(
        this.packagingFrozenId,
        this.packagingRefrigeratedId,
        this.packagingTemperatureId,
        this.packagingRefrigeratedDryId
      );
      this.icons.forEach((icon) => {
        icon.value = true;
      });

      const filtersValid = this.setFiltersFromUrl();
      this.cleanDate++;

      return filtersValid;
    },

    setFiltersFromUrl() {
      const query = this.$route.query;

      if (query.startDate && query.endDate) {
        const startDate = this.parseDate(query.startDate);
        const endDate = this.parseDate(query.endDate);

        const validation = this.validateDateRange(startDate, endDate);

        if (!validation.isValid) {
          this.resetToTodayAndUpdateUrl();
          notify({ title: "AVISO:", message: validation.errorMessage });
          return false;
        } else {
          this.dateSelected = {
            start: startDate,
            end: endDate,
          };

          this.dateQuery = `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
        }
      } else {
        this.dateQuery = `${moment(this.today).format("DD/MM/YYYY")} - ${moment(this.today).format("DD/MM/YYYY")}`;
      }

      if (query.dateType) {
        this.typeDateSelected = query.dateType;
      }

      if (query.packagingType) {
        this.packagingSelected = query.packagingType.split(",");
        this.icons.forEach((icon) => {
          icon.value = this.packagingSelected.includes(icon.id);
        });
      }

      return true;
    },

    getSelectedCollectionPoint() {
      if (!this.isAllowedCollectionPoint) return;

      const savedCollectionPoint = Cookies.get("selectedCollectionPoint");
      if (savedCollectionPoint) {
        this.$nextTick(() => {
          this.$refs.refSearchCollectionPoint.field = JSON.parse(savedCollectionPoint);
          this.collectionPoint = JSON.parse(savedCollectionPoint);
        });
      } else if (this.isAllowedCollectionPoint) {
        this.showModalCollectionPointModal = true;
      }
    },

    setDate(e) {
      if (this.isResetting) {
        return;
      }

      if (!e || !e.date) {
        return;
      }

      const date = {
        start: e.date.start,
        end: e.date.end,
      };

      if (!date.start || !date.end) {
        return;
      }

      const currentStart = this.dateSelected?.start;
      const currentEnd = this.dateSelected?.end;

      if (currentStart && currentEnd) {
        const isSameStart = moment(date.start).isSame(moment(currentStart), "day");
        const isSameEnd = moment(date.end).isSame(moment(currentEnd), "day");

        if (isSameStart && isSameEnd) {
          return;
        }
      }

      const validation = this.validateDateRange(date.start, date.end);

      if (!validation.isValid) {
        this.resetToTodayAndUpdateUrl();

        let errorMessage = "";
        if (!validation.isValidRange && !validation.isValidFromToday) {
          errorMessage = `Selecione um período de até ${validation.maxDays} dias e não anterior a ${validation.maxDays} dias atrás.`;
        } else if (!validation.isValidRange) {
          errorMessage = `Selecione um período de até ${validation.maxDays} dias.`;
        } else if (!validation.isValidFromToday) {
          errorMessage = `Não é possível selecionar datas anteriores a ${validation.maxDays} dias atrás.`;
        }

        notify({ title: "AVISO:", message: errorMessage });
      } else {
        this.dateSelected = date;
      }
    },

    setPackagingType(icon, value) {
      const { id } = icon;
      const allSelected = this.icons.every((item) => item.value);

      if (allSelected) {
        this.deselectAllExcept(id);
      } else {
        this.toggleSelection(icon, value);
      }

      this.submitSearch();
    },

    deselectAllExcept(id) {
      this.icons.forEach((item) => {
        item.value = item.id === id;
      });
      this.packagingSelected = [id];
    },

    toggleSelection(icon, value) {
      icon.value = value;
      const index = this.packagingSelected.indexOf(icon.id);

      if (index === -1) {
        this.packagingSelected.push(icon.id);
      } else {
        this.packagingSelected.splice(index, 1);
      }
    },

    setCollectionPoint(event) {
      if (event) {
        Cookies.set("selectedCollectionPoint", JSON.stringify(event), { expires: 7 });
        this.collectionPoint = event;
        this.$refs.refSearchCollectionPoint.field = this.collectionPoint;
      } else {
        Cookies.remove("selectedCollectionPoint");
        this.collectionPoint = null;
      }
    },

    setCollectionPointModal(event) {
      if (event) {
        this.showModalCollectionPointModal = false;
        this.$nextTick(() => {
          this.setCollectionPoint(event);
          this.submitSearch();
        });
      } else {
        Cookies.remove("selectedCollectionPoint");
        this.collectionPoint = null;
      }
    },

    async submitSearch() {
      if (this.dateSelected?.start && this.dateSelected?.end) {
        const validation = this.validateDateRange(this.dateSelected.start, this.dateSelected.end);

        if (!validation.isValid) {
          this.resetToTodayAndUpdateUrl();
          notify({ title: "AVISO:", message: validation.errorMessage });
          await this.$nextTick();
        }
      }

      try {
        this.selectBatchList(this.batchFilter);
        await this.batchGetBatches();
      } catch (error) {
        this.handleBackendError(error);
      }
    },

    hintPackagingType(value) {
      const packagingSelected = this.packagingSelected;
      const hintSelect = "Selecione para filtrar uma ou mais formas de acondicionamento";
      const hintDeselect = "Clique para remover filtro";

      if (packagingSelected?.length === 4 && value) return hintSelect;

      if (value) return hintDeselect;

      return hintSelect;
    },

    parseDate(dateStr) {
      return moment(dateStr, "DD/MM/YYYY").toDate();
    },

    validateDateRange(startDate, endDate) {
      const maxDays = this.limitDaysBatches;
      const diffDays = moment(endDate).diff(moment(startDate), "days");
      const daysFromToday = moment().diff(moment(startDate), "days");

      const isValidRange = diffDays <= maxDays;
      const isValidFromToday = daysFromToday <= maxDays;
      const isValid = isValidRange && isValidFromToday;

      let errorMessage = "";
      if (!isValidRange && !isValidFromToday) {
        errorMessage = `Período ajustado - máximo ${maxDays} dias e não anterior a ${maxDays} dias atrás.`;
      } else if (!isValidRange) {
        errorMessage = `Período ajustado para respeitar o limite de ${maxDays} dias para lotes.`;
      } else if (!isValidFromToday) {
        errorMessage = `Período ajustado - não é possível buscar lotes anteriores a ${maxDays} dias atrás.`;
      }

      return {
        isValid,
        isValidRange,
        isValidFromToday,
        diffDays,
        daysFromToday,
        maxDays,
        errorMessage,
      };
    },

    resetToTodayAndUpdateUrl() {
      this.isResetting = true;

      const newStartDate = formatDate(this.today, 4);
      const newEndDate = formatDate(this.today, 4);

      const currentQuery = this.$route.query;
      if (currentQuery.startDate !== newStartDate || currentQuery.endDate !== newEndDate) {
        this.$router
          .replace({
            path: this.$route.path,
            query: {
              ...this.$route.query,
              startDate: newStartDate,
              endDate: newEndDate,
            },
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("Erro na navegação:", err);
            }
          });
      }

      this.dateSelected = {
        start: this.today,
        end: this.today,
      };

      this.dateQuery = `${moment(this.today).format("DD/MM/YYYY")} - ${moment(this.today).format("DD/MM/YYYY")}`;

      this.cleanDate++;

      this.$nextTick(() => {
        this.isResetting = false;
      });
    },

    handleBackendError(error) {
      console.log(error);
      if (error?.response?.data) {
        const errorData = error.response.data;

        if (errorData.code === "INVALID_RANGE_PARAMETER") {
          this.resetToTodayAndUpdateUrl();

          const message = errorData.message || "Período inválido para a busca.";
          notify({ title: "AVISO:", message: `${message} Período ajustado para hoje.` });
        } else {
          const message = errorData.message || "Erro ao buscar lotes.";
          notify({ title: "AVISO:", message });
        }
      } else {
        notify({ title: "AVISO:", message: "Erro ao buscar lotes. Tente novamente." });
      }
    },

    getCurrentFilters() {
      const filters = {};

      if (this.startDate) filters.startDate = this.startDate;
      if (this.endDate) filters.endDate = this.endDate;
      if (this.typeDateSelected) filters.dateType = this.typeDateSelected;
      if (this.packagingSelected.length > 0) filters.packagingType = this.packagingSelected.join(",");

      return filters;
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.card-form__container) {
  padding-top: 8px !important;
}

.w-100 {
  width: 100%;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;

  div {
    flex: 1 1 auto;
    margin: 0;
  }
}
</style>
